import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { WINDOW } from '@df/dom/globals';
import { Renderer } from '@df/ng/renderer';
import { BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';

/**
 * Inject atomic stylesheets files and add custom classes to the document body.
 *
 * @export
 * @class StylesService
 */
@Injectable({
    providedIn: 'root'
})
export class StylesService {
    // protected version: string;

    readonly scale$ = new BehaviorSubject<number>(1);

    private initiated = false;
    /**
     * Provides value in vw units that can safely be used on a website
     * in client app
     * 12 will be converted to 12vw
     * in admin app
     * 12 will be converted to calc(var(--vw)*12)
     */
    static vw(value: number): string {
        return `calc(var(--vw, 1vw)*${value})`;
    }
    static vh(value: number): string {
        return `calc(var(--vh, 1vh)*${value})`;
    }

    protected subscriptions = new Subscription();

    private window = inject(WINDOW);

    constructor(
        private renderer: Renderer,
        private client: Client,
        private app: App,
        @Inject(DOCUMENT) private document: Document
    ) {}

    appInitialize() {
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.setSizeClass()));
        this.subscriptions.add(this.client.obs.resize.subscribe(() => this.setViewportSizeVars()));

        if (this.app.isPlatformBrowser) {
            this.subscriptions.add(
                merge(fromEvent(window as Window, 'resize'), fromEvent(window as Window, 'DOMContentLoaded'), this.scale$).subscribe(() =>
                    this.setViewportSizeVars()
                )
            );
        }

        this.initiated = true;

        this.setViewportSizeVars();
        this.setSizeClass();
        this.setCustomClass();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.scale$.complete();
    }

    protected setSCrollTop() {
        this.subscriptions.add(
            this.client.obs.scroll.subscribe(() => {
                this.setCssVar('--scrollTop', `${this.client.getRootElement().scrollTop || 0}px`);
            })
        );
    }

    /**
     * Set custom classes.
     *
     * @memberof StylesService
     */
    private setCustomClass() {
        // set touchy related classes
        const isTouchy = this.client.isTouchy;

        this.renderer.addClass(this.document.body, isTouchy ? 'is-touchy' : 'is-not-touchy');
        this.renderer.removeClass(this.document.body, isTouchy ? 'is-not-touchy' : 'is-touchy');
    }

    /**
     * Updates size class on body element.
     *
     * @protected
     * @memberof StylesService
     */
    private setSizeClass() {
        if (!this.client.size) {
            return;
        }
        const appSize: string = this.client.size.toLowerCase();
        ['s', 'm', 'l', 'x'].forEach(size => {
            this.renderer.setClass(this.document.body, 'size-' + size, size === appSize);
        });
    }

    private setViewportSizeVars() {
        if (this.initiated && this.app.isPlatformBrowser && !this.client.isPrerender) {
            const windowHeight = this.window.innerHeight;
            const height = Math.min(windowHeight, this.client.height);
            this.setCssVar('--vh', `${Math.round((this.client.isCms ? height : windowHeight) / this.scale$.value) / 100}px`);
            this.setCssVar('--vw', this.client.isCms ? `${Math.round(this.client.width / this.scale$.value) / 100}px` : '1vw');
            this.setCssVar('--window-height', `min(100vh, ${windowHeight}px)`);
        }
    }

    setCssVar(name: string, value: string | undefined, target: HTMLElement = this.document.documentElement) {
        if (typeof value === 'undefined') {
            target.style.removeProperty(name);
        } else {
            target.style.setProperty(name, value);
        }
    }
}
